import { Button } from "@tokenterminal/core/ui/button/Button"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import { Combobox } from "@tokenterminal/core/ui/samsCombobox/Combobox"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
import { useAvailableEcosystemMetricIds } from "modules/projects/hooks/useAvailableEcosystemMetricIds"
import { useMetricsConfigurationList } from "modules/projects/hooks/useMetricsConfigurationList"
import IntervalSelectField from "modules/projects/MetricsTab/IntervalSelectField"
import { useMemo, useState } from "react"
import useMetricsConfigurationBySlug from "../DetailPage/hooks/useMetricsConfigurationBySlug"
import { toSlug } from "../utils"
import { MarketSectorsTable } from "./MarketSectorsTable"
import { MarketSectorsTreemapCard } from "./MarketSectorsTreemapCard"
import { MetricsDropdown } from "./MetricsDropdown"

const InnerComponent = () => {
  const [interval, setInterval] = useState<Interval>("365d")
  const [selectedMetricId, setSelectedMetricId] = useState("fees")

  // Data
  const {
    data: ecosystemMetricIds = [],
    isLoading: isLoadingEcosystemMetricIds,
  } = useAvailableEcosystemMetricIds()
  const {
    data: metricConfigurations = [],
    isLoading: isLoadingMetricConfigurations,
  } = useMetricsConfigurationList()
  const metricConfiguration = useMetricsConfigurationBySlug(
    toSlug(selectedMetricId)
  )
  const isLoading = isLoadingEcosystemMetricIds || isLoadingMetricConfigurations

  // Exclude ecosystem metrics
  const availableMetrics = useMemo(() => {
    return metricConfigurations.filter(
      (metric) => !ecosystemMetricIds?.includes(metric.metric_id)
    )
  }, [metricConfigurations, ecosystemMetricIds])

  if (isLoading) {
    return null
  } else if (!metricConfiguration) {
    return "Couldn't find metric"
  }

  return (
    <div className="w-full relative pt-4 md:pt-6 mb-10 border-t border-solid border-border-default">
      <div className="w-[1440px] px-4 max-w-full mx-auto flex flex-col gap-5">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
            <Heading level={2} className="block md:hidden">
              Market sectors
            </Heading>
            <Heading level={1} className="hidden md:block">
              Market sectors
            </Heading>
          </div>
          <div className="flex flex-row items-center gap-2">
            <MetricsDropdown
              availableMetrics={availableMetrics}
              selectedMetricId={selectedMetricId}
              onChangeSelectedMetricId={setSelectedMetricId}
              trigger={
                <Button>
                  <span className="text-fg-secondary">Ranked by</span>
                  {metricConfiguration?.title}
                  <Combobox.DropdownIcon />
                </Button>
              }
            />
            <Divider orientation="vertical" className="hidden md:block" />
            <IntervalSelectField
              selectedKey={interval}
              setInterval={setInterval}
            />
          </div>
        </div>
        <MarketSectorsTreemapCard
          interval={interval}
          metricConfiguration={metricConfiguration}
        />
        <div className="flex flex-col gap-4 w-full mt-4">
          <MarketSectorsTable
            metricConfiguration={metricConfiguration}
            interval={interval}
          />
        </div>
      </div>
    </div>
  )
}

export function MarketSectorsListPage() {
  return (
    <ClientOnlySuspense fallback={null}>
      <InnerComponent />
    </ClientOnlySuspense>
  )
}
